import React, { useEffect, useState } from "react";
import { Lesson, LessonState } from "../../types/Lessons";
import Api from "../../network/Api";
import Summary from "./Summary";
import { Alert, Box, Card, Container, FormGroup, FormLabel, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Stack } from "@mui/material";
import User from "../../types/User";
import Toolbar from "./Toolbar";
import CommentBox from "../Comments/CommentBox";
import { EditableTextField } from "../Shared/FormItems";
import Linkify from "linkify-react";
import Placeholder from "../Shared/Placeholder";
import { NavLink } from "react-router-dom";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SplitRow from "../Shared/SplitRow";
import i18n from "i18next";

type LessonParams = {
  mitigation?: string;
  approver?: User | string;
  time_lost?: string;
};

type ChangePropObject = {[key: string]: string|number|null|object};

export type ChangeProps = {
  onChange: (object: ChangePropObject) => void;
  onSave: (object: ChangePropObject) => void;
};

const Show = (props: { id: string, user: User }) => {

  const [lesson, setLesson] = useState<Lesson>();

  useEffect(() => {
    Api.get(`/lessons/${props.id}`).then(setLesson);
  }, []);

  const [params, setParams] = useState<LessonParams>({});
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [editable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    if (typeof(lesson) === "undefined") { return }
    setParams({...params, ...lesson});
    if (LessonState[lesson.state] !== LessonState.approved) {
      setIsEditable(LessonState[lesson.state] === LessonState.draft || lesson.approver?.id === props.user.id);
    }
  }, [lesson]);

  const save = (params: LessonParams) => {
    setIsUpdating(true);
    Api.put(`/lessons/${lesson?.id}`, params).then(setSolutionResults);
  }

  const review = () => {
    setIsUpdating(true);
    Api.put(`/lessons/${lesson?.id}`, {state: LessonState[LessonState.review] }).then(setSolutionResults);
  }
  const accept = () => {
    setIsUpdating(true);
    Api.put(`/lessons/${lesson?.id}`, {state: LessonState[LessonState.approved] }).then(setSolutionResults);
  }
  const reject = (comment?: string) => {
    setIsUpdating(true);
    if (comment) {
      Api.post(`/lessons/${lesson?.id}/comments`, { content: comment, color: 'error' });
    }
    Api.put(`/lessons/${lesson?.id}`, {state: LessonState[LessonState.rejected] }).then(result => window.location.reload());
  }
  const reopen = () => {
    setIsUpdating(true);
    Api.put(`/lessons/${lesson?.id}`, {state: LessonState[LessonState.draft] }).then(result => window.location.reload());
  }

  const setSolutionResults = (result) => {
    setLesson(result);
    setIsUpdating(false);
  }

  return <Box sx={{flex: 1, overflowY: 'auto', py: 3, width: "100%"}}>
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        <Grid size={{xs: 12, md: 8}}>
          <Card>
            { lesson ? <Summary lesson={lesson} 
                                editable={editable}
                                onChange={object => setParams({...params, ...object})}
                                onSave={save} /> : <Placeholder /> }
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 4}}>
          <Stack spacing={3}>
            <Paper sx={{p: 3}}>{ lesson ? <>
              <FormGroup>
                <p><strong>Review</strong></p>
              </FormGroup>
              <Toolbar lesson={lesson}
                        user={props.user}
                        isUpdating={isUpdating}
                        onChange={object => setParams({...params, ...object})}
                        onSave={save}
                        onReview={review}
                        onAccept={accept}
                        onReject={reject}
                        onReopen={reopen} />
              </> : <Placeholder /> }
            </Paper>

            { lesson?.solutions && lesson.solutions.length > 0 && <>
            <Paper sx={{p: 3}}>{ lesson ? <>
              <FormGroup>
                <p><strong>Linked solution</strong></p>
                <List>
                  { lesson.solutions.map(solution => {
                    return <ListItem key={solution.id} sx={{p: "0 !important"}}>
                      <ListItemText>
                        <ListItemButton component={NavLink} to={`/solutions/${solution.id}`} sx={{p: "0.5rem 1rem !important"}}>
                          <ListItemIcon sx={{minWidth: '0 !important', mr: 2}}><AccountTreeIcon color="secondary" fontSize="small" /></ListItemIcon>
                          {solution.issue}
                        </ListItemButton>
                      </ListItemText>
                    </ListItem>
                  }) }
                </List>
              </FormGroup>
              </> : <Placeholder /> }
            </Paper>
            </> }

            <Paper sx={{ p: 3 }}>{ lesson ? <>
              <Grid container spacing={2} alignItems="center">
                <SplitRow label="Single time lost">
                { params && <>{ LessonState[lesson.state] !== LessonState.approved ?
                  <EditableTextField
                      multiline={false}
                      placeholder="Estimated time lost caused by this issue"
                      type="number"
                      value={params?.time_lost ?? ""}
                      onChange={value => setParams({...params, time_lost: value})}
                      onSave={value => save({time_lost: value})}
                      disabled={!editable}
                      endAdornment={<FormLabel>minutes</FormLabel>}
                    /> : `${lesson.time_lost} minutes`}</> }
                </SplitRow>

                <SplitRow label="Annual time lost">
                  <strong className="text-error">{lesson.annual_time_lost} minutes</strong>
                </SplitRow>
              
                <SplitRow label="Mitigation proposal">
                  { params && <>{ LessonState[lesson.state] !== LessonState.approved ?
                    <EditableTextField
                        required
                        placeholder="Describe the result e.g. add a mitigation or insert a link to any useful resource that helps resolving the issue."
                        value={params?.mitigation ?? ""}
                        onChange={value => setParams({...params, mitigation: value})}
                        onSave={value => save({mitigation: value})}
                        disabled={!editable}
                      /> : lesson.mitigation && <Alert><Linkify options={{target: "_blank"}}>{lesson.mitigation}</Linkify></Alert> } </> }
                  </SplitRow>
                </Grid>
              </> : <Placeholder /> }
            </Paper>
            
            <Paper sx={{p: 3}}>{ lesson ? <>
              <FormGroup>
                <p><strong>Comments</strong></p>
              </FormGroup>                  
              <CommentBox user={props.user} lesson={lesson} />
              </> : <Placeholder /> }
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  </Box>
}

export default Show;